import { FC, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { userActions } from '@/redux/actions';
import { userSelectors } from '@/redux/selectors';
import { useApiCall, useInputText } from '@/hooks';
import {
  ResetPasswordRequest,
  VerifyRecoveryTokenRequest,
  VerifyRecoveryTokenResponse,
  amlbotAPI,
} from '@/api';
import { Validation } from '@/models';
import { COMMON_CONTAINER_ID, ErrorCodeEnum, PASSWORD_REGEX } from '@/config';

import {
  AuthContainer,
  AlertBox,
  Input,
  Button,
  Loader,
  Support,
} from '@/components';

import styles from './PasswordReset.module.sass';

interface PasswordResetProps {}

const PasswordReset: FC<PasswordResetProps> = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { error, loading } = useAppSelector(userSelectors.getUserSlice);

  const token = searchParams.get('token') || '';

  const [
    verifyRecoveryToken,
    veryfyRecoveryTokenLoading,
    veryfyRecoveryTokenError,
    veryfyRecoveryTokenErrorCode,
  ] = useApiCall(async () => {
    const payload: VerifyRecoveryTokenRequest = {
      token,
    };

    // Optionally [errorCode] - ErrorCodeEnum.FIELD_REQUIRED
    const response: VerifyRecoveryTokenResponse =
      await amlbotAPI.verifyRecoveryToken(payload);
  });

  useEffect(() => {
    dispatch(userActions.setError(null));
    verifyRecoveryToken();
  }, []);

  const PASSWORD_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'regex',
        value: PASSWORD_REGEX,
        message: t('translation.reset_pwd_error_hint_length'),
      },
    ],
    [t],
  );

  const passwordField = useInputText('', PASSWORD_VALIDATIONS);

  const PASSWORD_CONFIRM_VALIDATIONS: Validation[] = useMemo(() => {
    return [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'equal',
        value: passwordField.value,
        message: t('translation.password_mismatch'),
      },
    ];
  }, [passwordField.value, t]);

  const passwordConfirmField = useInputText('', PASSWORD_CONFIRM_VALIDATIONS);

  const submitHandler = () => {
    if (!loading) {
      if (passwordField.isValid && passwordConfirmField.isValid) {
        if (token) {
          const payload: ResetPasswordRequest = {
            password: passwordField.value,
            token,
          };

          dispatch(userActions.resetPassword(payload));
        } else {
          toast.error(t('translation.token_is_missing'), {
            containerId: COMMON_CONTAINER_ID,
          });
        }
      } else {
        if (!passwordField.isValid) {
          passwordField.onBlur();
        }

        if (!passwordConfirmField.isValid) {
          passwordConfirmField.onBlur();
        }
      }
    }
  };

  const tokenErrorMessage = (errorCode: ErrorCodeEnum | null) => {
    if (errorCode === ErrorCodeEnum.FIELD_REQUIRED) {
      return t('translation.token_is_missing');
    }

    if (veryfyRecoveryTokenErrorCode === ErrorCodeEnum.TOKEN_EXPIRED) {
      return t('translation.token_expired');
    }

    return t('translation.token_invalid');
  };

  return (
    <>
      <Helmet>
        <title>AMLBot - {t('translation.reset_pwd_title')}</title>
      </Helmet>
      <AuthContainer
        title={t('translation.reset_pwd_title')}
        description={
          <Link to="/signin" className="link">
            {t('translation.back_to_login_btn')}
          </Link>
        }
        alert={
          !!error && (
            <AlertBox type="danger">
              {{ description: error, icon: '🤔' }}
            </AlertBox>
          )
        }
        fields={
          <>
            {veryfyRecoveryTokenLoading && <Loader />}
            {!veryfyRecoveryTokenLoading && (
              <>
                {veryfyRecoveryTokenError && (
                  <>
                    <AlertBox type="danger">
                      {{
                        title: parse(t('translation.error')),
                        description: (
                          <div>
                            {tokenErrorMessage(veryfyRecoveryTokenErrorCode)}
                          </div>
                        ),
                        icon: '🤔',
                      }}
                    </AlertBox>
                    <AlertBox type="info">
                      {{
                        title: t('translation.tips'),
                        description: (
                          <div className={styles.tips}>
                            <ul className={styles.list}>
                              <li>
                                <div className={styles.wrapper}>
                                  <span className={styles.gap}>
                                    {t('translation.contact_support')}
                                  </span>
                                  <Support />
                                </div>
                              </li>
                            </ul>
                          </div>
                        ),
                      }}
                    </AlertBox>
                  </>
                )}
                {!veryfyRecoveryTokenError && (
                  <>
                    <div>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={passwordField.value as string}
                        onChange={passwordField.onChange}
                        onBlur={passwordField.onBlur}
                        isError={
                          passwordField.isDirty && !passwordField.isValid
                        }
                        placeholder={t('translation.password') || ''}
                        disabled={loading}
                        required
                        floating
                      />
                      {passwordField.isDirty && !passwordField.isValid && (
                        <AlertBox type="danger" small>
                          {{ description: passwordField.errors[0] }}
                        </AlertBox>
                      )}
                    </div>

                    <div>
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={passwordConfirmField.value as string}
                        onChange={passwordConfirmField.onChange}
                        onBlur={passwordConfirmField.onBlur}
                        isError={
                          passwordConfirmField.isDirty &&
                          !passwordConfirmField.isValid
                        }
                        placeholder={t('translation.confirm_password') || ''}
                        disabled={loading}
                        required
                        floating
                      />
                      {passwordConfirmField.isDirty &&
                        !passwordConfirmField.isValid && (
                          <AlertBox type="danger" small>
                            {{ description: passwordConfirmField.errors[0] }}
                          </AlertBox>
                        )}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        }
        buttons={
          <div>
            {!veryfyRecoveryTokenError && !veryfyRecoveryTokenLoading && (
              <Button
                size="xl"
                corner="rect"
                onClick={submitHandler}
                disabled={loading}
                fullwidth
              >
                {t('translation.submit_btn')}
              </Button>
            )}
          </div>
        }
      />
    </>
  );
};

export default PasswordReset;
